import type {
  PrCreationStep,
  RejectionSection,
  RequestStatus,
  SubmitRequestInput,
} from '@pr-app/shared';
import { api } from './client';
import type {
  DashboardDateRange,
  DashboardSortField,
  DashboardSortOrder,
  RequestCounts,
  RequestSummary,
} from './requests';

export interface ReviewQueueRequest {
  id: string;
  requestNo: number;
  status: string;
  purchaseType: string | null;
  companyCode: string | null;
  vendorAccount: string | null;
  currencyCode: string | null;
  requesterName: string;
  requesterEmail: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  /** True for the viewer's own request — present in the queue, but not actionable. */
  isOwn: boolean;
  lineCount: number;
  attachmentCount: number;
  grandTotal: string;
  /** D365 refused the last approval and the requester has not resubmitted — fix and approve again. */
  lastAttemptFailed: boolean;
}

/** Search box and company filter (V1) — both optional, applied server-side. */
export interface ReviewQueueParams {
  q?: string;
  company?: string;
}

function buildQueueQuery(params: ReviewQueueParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.company) search.set('company', params.company);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Wireframe V1 — every PENDING_REVIEW request, oldest first, shared across the
 * whole reviewer group. `q` matches requester, vendor or request no; `company`
 * filters to one company code — both optional and applied server-side.
 */
export const getReviewQueue = (params: ReviewQueueParams = {}): Promise<ReviewQueueRequest[]> =>
  api<{ requests: ReviewQueueRequest[]; companies: string[] }>(
    `/api/review/queue${buildQueueQuery(params)}`,
  ).then((data) => data.requests);

/**
 * #222 — for a reviewer, "My Requests" becomes "Dashboard": the pending queue
 * plus every request the viewer has personally approved or rejected, read-only.
 * Same request/response shape as `listRequests` (`ListRequestsParams`,
 * `ListRequestsResult`) so `Dashboard.tsx` can drive either data source with no
 * branching beyond which one it calls — the only addition is `isOwn` on each
 * row, present the same way `getReviewQueue`'s own rows already carry it.
 */
export interface ReviewDashboardParams {
  q?: string;
  status?: RequestStatus[];
  company?: string[];
  range?: DashboardDateRange;
  from?: string;
  to?: string;
  sort?: DashboardSortField;
  order?: DashboardSortOrder;
  page?: number;
  pageSize?: number;
}

export interface ReviewDashboardResult {
  requests: (RequestSummary & { isOwn: boolean })[];
  total: number;
  page: number;
  pageSize: number;
  counts: RequestCounts;
  companies: string[];
}

function buildDashboardQuery(params: ReviewDashboardParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.status && params.status.length > 0) search.set('status', params.status.join(','));
  if (params.company && params.company.length > 0) search.set('company', params.company.join(','));
  if (params.range) search.set('range', params.range);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.sort) search.set('sort', params.sort);
  if (params.order) search.set('order', params.order);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const getReviewDashboard = (
  params: ReviewDashboardParams = {},
): Promise<ReviewDashboardResult> =>
  api<ReviewDashboardResult>(`/api/review/dashboard${buildDashboardQuery(params)}`);

/** The result of a reviewer's decision — wireframes V3 (reject) / V4-V6 (approve). */
export interface DecisionResult {
  decision: 'APPROVED' | 'REJECTED';
  /**
   * The D365 attempt outcome (wireframes V5 "creating…" / V6 "PR created") —
   * `null` for a rejection, which never touches D365. Returned inline because
   * the attempt has already run by the time this resolves, so the caller
   * never has to guess or poll to show V6's PR number.
   */
  prCreation: PrAttemptResult | null;
}

/**
 * Wireframes V3 (reject) / V4 (approve) — a rejection's comment is mandatory
 * server-side too; an approval's comment is optional.
 *
 * An approval also attempts the D365 requisition server-side (#46) and can take
 * several seconds — this call does not resolve until that attempt finishes, one
 * way or the other. The approval stands either way even if `prCreation.outcome`
 * is `'FAILED'`: that failure also shows up in `getPrFailures()`.
 *
 * `section` (#155, V3) is reject-only and optional — which part of the request
 * the rejection is about, surfaced back to the requester as R8's badge.
 */
export const decideRequest = (
  id: string,
  decision: 'APPROVE' | 'REJECT',
  comment?: string,
  section?: RejectionSection,
): Promise<DecisionResult> =>
  api<DecisionResult>(`/api/review/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    body: JSON.stringify({ decision, comment, section }),
  });

/** An approved request whose D365 creation failed — wireframe 05.2 (V7). */
export interface PrFailure {
  id: string;
  requestNo: number;
  status: string;
  purchaseType: string | null;
  companyCode: string | null;
  vendorAccount: string | null;
  currencyCode: string | null;
  requesterName: string;
  requesterEmail: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  /** Raw D365 message from the most recent attempt, shown verbatim (V7 note 3). */
  lastError: string;
  attemptCount: number;
  lastAttemptAt: string;
}

export const getPrFailures = (): Promise<PrFailure[]> =>
  api<{ requests: PrFailure[] }>('/api/review/pr-failures').then((data) => data.requests);

/** A failed attempt is a 200 carrying the D365 message, not an HTTP error — see the route's docs. */
export type PrAttemptResult =
  | {
      outcome: 'CREATED';
      prNumber: string;
      /**
       * Set when the requisition was created but its files could not be linked
       * into D365 (#132) — the request is PR_CREATED all the same, and the gap
       * lives on as a `PR_ATTACHMENTS_FAILED` audit row.
       */
      attachmentFailure: string | null;
    }
  | {
      outcome: 'FAILED';
      message: string;
      /**
       * Which sub-step failed (#44, wireframe V5) — `HEADER` or `LINES` since
       * #132 made the attachment step non-blocking. `ATTACHMENTS` only appears
       * on attempts recorded before that.
       */
      step: PrCreationStep | null;
    };

/** V7 primary action. Idempotent server-side: it can never create a second requisition. */
export const retryPrCreation = (id: string): Promise<PrAttemptResult> =>
  api<PrAttemptResult>(`/api/review/${encodeURIComponent(id)}/retry-pr-creation`, {
    method: 'POST',
  });

/** V7 secondary action — back to REJECTED, with the D365 error as the reason. */
export const sendBackToRequester = (id: string): Promise<void> =>
  api<void>(`/api/review/${encodeURIComponent(id)}/send-back`, { method: 'POST' });

/** V7 lowest-emphasis action — nothing notifies the requester unless a reviewer does. */
export const notifyRequester = (id: string): Promise<void> =>
  api<void>(`/api/review/${encodeURIComponent(id)}/notify-requester`, { method: 'POST' });

/**
 * Saves a reviewer's edits to a PENDING_REVIEW request — `PUT /api/review/:id`.
 * Same body and validation as the requester's submit, so a reviewer cannot save
 * something a requester could not have submitted.
 */
export const saveReviewerEdits = (id: string, payload: SubmitRequestInput): Promise<unknown> =>
  api<unknown>(`/api/review/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
